import { useEffect, useState } from "react";
import { fetchAttachmentUrl, fetchAttachments, formatBytes, type Attachment, type AttachmentScope } from "../services/attachmentsService";

// ===========================================================================
// Documentos de un negocio / contacto: los archivos que HubSpot tenía adjuntos a
// sus notas, correos, llamadas, reuniones y tareas, ya copiados al CRM. Se abren
// con un enlace firmado que caduca. Con `onPick` cada archivo se puede elegir
// (p. ej. como documento de una cotización).
// ===========================================================================

interface Props extends AttachmentScope {
  textClass: string;
  mutedClass: string;
  borderClass: string;
  /** When given, each available file shows this action. */
  onPick?: (attachment: Attachment) => void;
  pickLabel?: string;
  /** The attachment currently picked, to mark it. */
  pickedId?: string | null;
}

const fmtDate = (iso: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "";
};

const iconFor = (a: Attachment) => {
  const name = a.name.toLowerCase();
  if (name.endsWith(".pdf")) return "ri-file-pdf-2-line";
  if (/\.(xlsx?|csv)$/.test(name)) return "ri-file-excel-2-line";
  if (/\.(docx?)$/.test(name)) return "ri-file-word-2-line";
  if (/\.(pptx?)$/.test(name)) return "ri-file-ppt-2-line";
  if (/\.(png|jpe?g|gif|webp)$/.test(name)) return "ri-image-line";
  return "ri-attachment-2";
};

export function AttachmentsList({ dealId, contactId, companyId, textClass, mutedClass, borderClass, onPick, pickLabel = "Usar", pickedId }: Props) {
  const [items, setItems] = useState<Attachment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setError(null);
    fetchAttachments({ dealId, contactId, companyId })
      .then((list) => alive && setItems(list))
      .catch(() => alive && setError("No se pudieron cargar los documentos"));
    return () => {
      alive = false;
    };
  }, [dealId, contactId, companyId]);

  const open = async (a: Attachment) => {
    setOpening(a.id);
    setError(null);
    try {
      window.open(await fetchAttachmentUrl(a.id), "_blank", "noopener,noreferrer");
    } catch {
      setError(`No se pudo abrir «${a.name}»`);
    } finally {
      setOpening(null);
    }
  };

  if (error && !items) return <p className="text-sm text-red-500">{error}</p>;
  if (!items) {
    return (
      <p className={`text-sm ${mutedClass}`}>
        <i className="ri-loader-4-line mr-1 animate-spin" />
        Cargando documentos...
      </p>
    );
  }
  if (!items.length) {
    return (
      <p data-testid="attachments-empty" className={`text-sm ${mutedClass}`}>
        Sin documentos. Los adjuntos de HubSpot se traen desde Configuración → HubSpot → «Importar adjuntos».
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="attachments-list">
      {items.map((a) => (
        <div key={a.id} data-testid={`attachment-${a.id}`} className={`flex flex-wrap items-center gap-2 rounded-lg border p-2 ${borderClass}`}>
          <i className={`${iconFor(a)} text-lg text-primary-500`} />
          <div className="min-w-0 flex-1">
            <p className={`truncate text-sm font-medium ${textClass}`} title={a.name}>
              {a.name}
            </p>
            <p className={`text-[11px] ${mutedClass}`}>
              {[a.sourceLabel, fmtDate(a.attachedAt), formatBytes(a.size), a.viaContact ? "del contacto" : ""].filter(Boolean).join(" · ")}
            </p>
          </div>
          {a.available ? (
            <>
              <button
                onClick={() => open(a)}
                disabled={opening === a.id}
                className="rounded-md px-2 py-1 text-xs font-medium text-primary-500 hover:bg-primary-500/10 disabled:opacity-50"
              >
                <i className={opening === a.id ? "ri-loader-4-line animate-spin" : "ri-external-link-line"} /> Ver
              </button>
              {onPick &&
                (pickedId === a.id ? (
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600">En uso</span>
                ) : (
                  <button onClick={() => onPick(a)} className="rounded-md bg-primary-500 px-2 py-1 text-xs font-medium text-white hover:bg-primary-400">
                    {pickLabel}
                  </button>
                ))}
            </>
          ) : (
            <span className={`text-[11px] ${mutedClass}`} title="Pesa más de 25 MB: no se copió al CRM">
              Sólo en HubSpot
            </span>
          )}
        </div>
      ))}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
